import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  FileText,
  Loader2,
  RefreshCw,
  ExternalLink,
  Sparkles,
  ChevronDown,
  Download,
  Info,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UploadModal from "../components/UploadModal";
import FloatingChat from "../components/FloatingChat";
import FeedbackPrompt from "../components/FeedbackPrompt";
import RenewalReminderCard from "../components/RenewalReminderCard";
import SEO from "../components/SEO";
import { getAnalysis, retryAnalysis } from "../lib/analysis";
import { downloadUrl, reportPdfUrl } from "../lib/uploads";
import { trackPolicyUploaded } from "../lib/analytics";

const NA = "not specified in your policy";

const SeverityBadge = ({ level }) => {
  const styles =
    level === "HIGH"
      ? "bg-red-100 text-red-800"
      : "bg-amber-100 text-amber-800";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase ${styles}`}
    >
      {level}
    </span>
  );
};

const BigScore = ({ value, reason }) => {
  const color =
    value >= 75 ? "#10B981" : value >= 55 ? "#F59E0B" : "#EF4444";
  const label = value >= 75 ? "Clear" : value >= 55 ? "Moderate" : "Complex";
  const r = 56;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="flex items-center gap-5">
      <svg width="144" height="144" viewBox="0 0 144 144">
        <circle cx="72" cy="72" r={r} stroke="#E2E8F0" strokeWidth="10" fill="none" />
        <circle
          cx="72"
          cy="72"
          r={r}
          stroke={color}
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 72 72)"
        />
        <text
          x="72"
          y="76"
          textAnchor="middle"
          fontSize="34"
          fontWeight="600"
          fill="#0F172A"
          fontFamily="Outfit"
        >
          {value}
        </text>
        <text
          x="72"
          y="98"
          textAnchor="middle"
          fontSize="10"
          fill="#64748B"
          fontFamily="DM Sans"
        >
          / 100
        </text>
      </svg>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">
          Clarity Score
        </div>
        <div className="font-display text-2xl font-semibold text-slate-900 mt-1">
          {label}
        </div>
        <p className="text-sm text-slate-600 mt-1 max-w-md leading-relaxed">
          {reason && reason !== NA
            ? reason
            : "Based on the actual text of your uploaded policy."}
        </p>
      </div>
    </div>
  );
};

const LoadingState = ({ filename, stage, textSource }) => {
  const stages = [
    { key: "queued", label: "Queued" },
    { key: "extracting", label: "Extracting text" },
    { key: "analysing", label: "Analysing clauses" },
    { key: "ready", label: "Ready" },
  ];
  const current =
    stage === "analysing" ? 2 : stage === "extracting" ? 1 : stage === "ready" ? 3 : 0;
  const activeLabel = stages[current]?.label ?? "Reading your policy";

  return (
    <div
      className="border border-slate-200 bg-white rounded-2xl p-8 md:p-12 text-center"
      data-testid="analysis-loading"
    >
      <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
      <h2
        className="mt-5 font-display text-xl font-semibold text-slate-900"
        data-testid="analysis-stage-label"
      >
        {activeLabel}…
      </h2>
      <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
        {stage === "extracting"
          ? "Reading the text out of your PDF. Scanned pages may take a little longer."
          : stage === "analysing"
          ? "Working through every clause of your policy for coverages, red flags and exclusions."
          : "Preparing your policy for analysis."}{" "}
        <span className="text-slate-700 font-medium">{filename || "your PDF"}</span>
      </p>

      {/* Stepper */}
      <ol
        className="mt-6 max-w-md mx-auto flex items-center gap-2"
        data-testid="analysis-stepper"
      >
        {stages.slice(0, 3).map((s, i) => {
          const state = i < current ? "done" : i === current ? "active" : "todo";
          return (
            <li key={s.key} className="flex-1" data-testid={`stage-${s.key}`}>
              <div
                className={`h-1.5 rounded-full ${
                  state === "done"
                    ? "bg-emerald-500"
                    : state === "active"
                    ? "bg-blue-600"
                    : "bg-slate-200"
                }`}
              />
              <div
                className={`mt-2 text-[11px] font-medium tracking-wide ${
                  state === "todo" ? "text-slate-400" : "text-slate-700"
                }`}
              >
                {s.label}
              </div>
            </li>
          );
        })}
      </ol>

      {textSource === "ocr" && stage === "analysing" && (
        <p
          className="mt-4 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-full inline-flex items-center gap-1.5 px-3 py-1"
          data-testid="ocr-badge"
        >
          <Sparkles className="h-3 w-3" />
          Scanned PDF detected — OCR used to read the text
        </p>
      )}
      {textSource === "photo" && stage === "analysing" && (
        <p
          className="mt-4 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-full inline-flex items-center gap-1.5 px-3 py-1"
          data-testid="photo-badge"
        >
          <Sparkles className="h-3 w-3" />
          Photo detected — reading with OCR
        </p>
      )}
    </div>
  );
};

const ErrorState = ({ error, onRetry, retrying }) => (
  <div
    className="border border-red-200 bg-red-50 rounded-2xl p-8 text-center"
    data-testid="analysis-error"
  >
    <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center text-red-600">
      <AlertTriangle className="h-6 w-6" />
    </div>
    <h2 className="mt-5 font-display text-xl font-semibold text-red-900">
      Analysis failed
    </h2>
    <p className="mt-2 text-sm text-red-700 max-w-lg mx-auto leading-relaxed">
      {error || "Something went wrong while analysing your policy."}
    </p>
    <button
      onClick={onRetry}
      disabled={retrying}
      data-testid="analysis-retry"
      className="mt-5 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60"
    >
      {retrying ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4" />
      )}
      Retry analysis
    </button>
  </div>
);

export default function PolicyAnalysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [openCoveredIdx, setOpenCoveredIdx] = useState(new Set());
  const [openFlagIdx, setOpenFlagIdx] = useState(new Set());
  const [openStdIdx, setOpenStdIdx] = useState(new Set());

  const toggleCovered = (i) =>
    setOpenCoveredIdx((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  const toggleFlag = (i) =>
    setOpenFlagIdx((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  const toggleStd = (i) =>
    setOpenStdIdx((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const fetchOnce = useCallback(async () => {
    try {
      const d = await getAnalysis(id);
      setData((prev) => {
        // Fire GA event exactly once, when the analysis first becomes complete.
        const wasComplete = prev?.status === "complete";
        if (!wasComplete && d.status === "complete") {
          try {
            trackPolicyUploaded({
              upload_id: id,
              insurer: d?.result?.insurer,
              clarity_score: d?.result?.clarity_score,
            });
          } catch (_) {}
        }
        return d;
      });
      setLoading(false);
      return d.status;
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      setLoading(false);
      return "not_found";
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const tick = async () => {
      if (cancelled) return;
      const status = await fetchOnce();
      if (cancelled) return;
      if (status === "pending" || status === "running") {
        timer = setTimeout(tick, 3000);
      }
    };
    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchOnce]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await retryAnalysis(id);
      setData((d) => ({ ...(d || {}), status: "running", error: null }));
      // Poll again
      setTimeout(fetchOnce, 2000);
    } finally {
      setRetrying(false);
    }
  };

  const status = data?.status;
  const r = data?.result;

  return (
    <div className="min-h-screen bg-slate-50/40">
      <SEO
        path={`/policy/${id}`}
        title="Policy analysis"
        description="A plain-English breakdown of your Indian motor insurance policy — covered items, red flags, standard terms, and a KYP clarity score."
        noindex
      />
      <Navbar onUploadClick={() => setUploadOpen(true)} />

      <div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10 pb-4"
        data-testid="policy-analysis-page"
      >
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          data-testid="policy-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </button>

        {notFound ? (
          <div className="mt-8 border border-slate-200 bg-white rounded-2xl p-10 text-center">
            <h1 className="font-display text-2xl font-semibold text-slate-900">
              This policy isn&apos;t in your session
            </h1>
            <p className="mt-2 text-slate-600 max-w-md mx-auto">
              Uploaded policies are private to the browser you uploaded them
              from. Upload a PDF to see its analysis.
            </p>
            <button
              onClick={() => setUploadOpen(true)}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-medium"
              data-testid="notfound-upload-btn"
            >
              Upload a policy
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div>
                <div className="text-xs text-slate-500">Policy Analysis</div>
                <h1
                  className="mt-1 font-display text-3xl md:text-4xl tracking-tight text-slate-900"
                  data-testid="analysis-title"
                >
                  {status === "complete" && r
                    ? `${r.insurer !== NA ? r.insurer : "Your policy"}${
                        r.vehicle && r.vehicle !== NA ? ` — ${r.vehicle}` : ""
                      }`
                    : data?.filename || "Your uploaded policy"}
                </h1>
                {status === "complete" && r?.period && r.period !== NA && (
                  <div className="mt-1 text-slate-500 text-sm">{r.period}</div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {status === "complete" && r?.policy_number && r.policy_number !== NA && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-medium">
                      <FileText className="h-3 w-3" />
                      {r.policy_number}
                    </span>
                  )}
                  {data?.filename && (
                    <a
                      href={downloadUrl(id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-medium hover:bg-blue-100"
                      data-testid="analysis-view-pdf"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View original PDF
                    </a>
                  )}
                  {status === "complete" && (
                    <a
                      href={reportPdfUrl(id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-medium hover:bg-slate-800"
                      data-testid="analysis-download-report-pdf"
                    >
                      <Download className="h-3 w-3" />
                      Download report as PDF
                    </a>
                  )}
                  {data?.text_source === "ocr" && status === "complete" && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 px-3 py-1 text-xs font-medium border border-amber-100"
                      data-testid="ocr-source-badge"
                    >
                      <Sparkles className="h-3 w-3" />
                      Read via OCR (scanned PDF)
                    </span>
                  )}
                  {data?.text_source === "photo" && status === "complete" && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 px-3 py-1 text-xs font-medium border border-amber-100"
                      data-testid="photo-source-badge"
                    >
                      <Sparkles className="h-3 w-3" />
                      Read from your photo (OCR)
                    </span>
                  )}
                </div>
              </div>
              {status === "complete" && (
                <div className="hidden lg:block text-xs text-slate-400 self-start pt-2">
                  Tap any item below to expand · Ask AI docked bottom-right
                </div>
              )}
            </div>

            {/* Body */}
            <div className="mt-8">
              {loading || status === "pending" || status === "running" ? (
                <LoadingState
                  filename={data?.filename}
                  stage={data?.stage}
                  textSource={data?.text_source}
                />
              ) : status === "failed" ? (
                <ErrorState
                  error={data?.error}
                  onRetry={handleRetry}
                  retrying={retrying}
                />
              ) : status === "complete" && r ? (
                <>
                  <div
                    className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                    data-testid="analysis-inline-disclaimer"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      <strong>Automated explanation — not insurance advice.</strong>{" "}
                      Verify with your insurer before making a claim.
                    </span>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="analysis-summary">
                    <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
                      <BigScore value={r.clarity_score} reason={r.clarity_reason} />
                      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                        <div className="rounded-xl bg-emerald-50 py-4">
                          <div className="font-display text-2xl font-semibold text-emerald-700" data-testid="covered-count">
                            {r.covered.length}
                          </div>
                          <div className="text-xs text-emerald-700/80 mt-0.5">
                            Items Covered
                          </div>
                        </div>
                        <div className="rounded-xl bg-red-50 py-4">
                          <div className="font-display text-2xl font-semibold text-red-700" data-testid="red-flag-count">
                            {r.red_flags.length}
                          </div>
                          <div className="text-xs text-red-700/80 mt-0.5">
                            Policy-specific
                          </div>
                        </div>
                        <div className="rounded-xl bg-slate-100 py-4">
                          <div className="font-display text-2xl font-semibold text-slate-700" data-testid="standard-terms-count">
                            {(r.standard_terms || []).length}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            Standard Terms
                          </div>
                        </div>
                        <div className="rounded-xl bg-slate-100 py-4">
                          <div className="font-display text-2xl font-semibold text-slate-700" data-testid="exclusion-count">
                            {r.exclusion_count}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">
                            Exclusions
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">
                        Policy Snapshot
                      </div>
                      <dl className="mt-4 space-y-3.5 text-sm">
                        {[
                          ["Policy No.", r.policy_number],
                          ["Insurer", r.insurer],
                          ["Vehicle", r.vehicle],
                          ["Period", r.period],
                          ["IDV", r.idv],
                          ["Premium Paid", r.premium],
                        ].map(([k, v]) => (
                          <div
                            key={k}
                            className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                          >
                            <dt className="text-slate-500">{k}</dt>
                            <dd
                              className={`text-right ${
                                v && v !== NA
                                  ? "text-slate-900 font-medium"
                                  : "text-slate-400 italic text-xs"
                              }`}
                            >
                              {v}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </div>

                  {/* Covered — accordion */}
                  <section className="mt-10" data-testid="covered-section">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                        <CheckCircle2 className="h-4 w-4" />
                      </div>
                      <h2 className="font-display text-xl font-semibold text-slate-900">
                        Covered — what this policy protects
                      </h2>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                      {r.covered.length === 0 ? (
                        <p className="p-6 text-sm text-slate-500 italic">{NA}</p>
                      ) : (
                        <ul className="divide-y divide-slate-100">
                          {r.covered.map((raw, i) => {
                            const item =
                              typeof raw === "string"
                                ? { title: raw, detail: "" }
                                : raw;
                            const isOpen = openCoveredIdx.has(i);
                            const hasDetail =
                              item.detail && item.detail.trim().length > 0;
                            return (
                              <li key={i} data-testid={`covered-item-${i}`}>
                                <button
                                  type="button"
                                  onClick={() => hasDetail && toggleCovered(i)}
                                  disabled={!hasDetail}
                                  className={`w-full flex items-start gap-3 px-4 md:px-5 py-3.5 text-left ${
                                    hasDetail
                                      ? "hover:bg-emerald-50/40 cursor-pointer"
                                      : "cursor-default"
                                  }`}
                                  data-testid={`covered-item-toggle-${i}`}
                                  aria-expanded={isOpen}
                                >
                                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                                  <span className="flex-1 text-sm text-slate-800 leading-snug">
                                    {item.title}
                                  </span>
                                  {hasDetail && (
                                    <ChevronDown
                                      className={`h-4 w-4 text-slate-400 mt-1 transition-transform ${
                                        isOpen ? "rotate-180" : ""
                                      }`}
                                    />
                                  )}
                                </button>
                                {isOpen && hasDetail && (
                                  <div
                                    className="pl-11 pr-5 pb-4 text-sm text-slate-600 leading-relaxed"
                                    data-testid={`covered-item-detail-${i}`}
                                  >
                                    {item.detail}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </section>

                  {/* Red Flags — accordion */}
                  <section className="mt-10" data-testid="red-flags-section">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-8 w-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                        <AlertTriangle className="h-4 w-4" />
                      </div>
                      <h2 className="font-display text-xl font-semibold text-slate-900">
                        Policy-specific concerns — read before you claim
                      </h2>
                    </div>
                    {r.red_flags.length === 0 ? (
                      <div
                        className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-800 flex items-start gap-2"
                        data-testid="no-red-flags-note"
                      >
                        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                        <span>
                          No policy-specific concerns found. This policy only carries
                          the standard statutory terms shown below.
                        </span>
                      </div>
                    ) : (
                      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        <ul className="divide-y divide-slate-100">
                          {r.red_flags.map((rf, i) => {
                            const isOpen = openFlagIdx.has(i);
                            return (
                              <li key={i} data-testid={`red-flag-${i}`}>
                                <button
                                  type="button"
                                  onClick={() => toggleFlag(i)}
                                  className="w-full flex items-start gap-3 px-4 md:px-5 py-3.5 text-left hover:bg-red-50/30"
                                  data-testid={`red-flag-toggle-${i}`}
                                  aria-expanded={isOpen}
                                >
                                  <SeverityBadge level={rf.severity} />
                                  <span className="flex-1 text-sm font-medium text-slate-900 leading-snug">
                                    {rf.title}
                                  </span>
                                  <ChevronDown
                                    className={`h-4 w-4 text-slate-400 mt-0.5 transition-transform ${
                                      isOpen ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                                {isOpen && (
                                  <div
                                    className="pl-4 md:pl-[4.75rem] pr-5 pb-4 space-y-1.5"
                                    data-testid={`red-flag-detail-${i}`}
                                  >
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                      {rf.detail}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      Reference: {rf.reference}
                                    </p>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </section>

                  {/* Standard statutory terms — informational, 0 score impact */}
                  {(r.standard_terms || []).length > 0 && (
                    <section className="mt-10" data-testid="standard-terms-section">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                          <Info className="h-4 w-4" />
                        </div>
                        <div>
                          <h2 className="font-display text-xl font-semibold text-slate-900">
                            Standard motor policy terms
                          </h2>
                          <p className="text-xs text-slate-500 mt-0.5">
                            These appear in virtually every Indian motor policy. Informational only — they do <strong>not</strong> lower your KYP score.
                          </p>
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        <ul className="divide-y divide-slate-100">
                          {r.standard_terms.map((rf, i) => {
                            const isOpen = openStdIdx.has(i);
                            return (
                              <li key={i} data-testid={`standard-term-${i}`}>
                                <button
                                  type="button"
                                  onClick={() => toggleStd(i)}
                                  className="w-full flex items-start gap-3 px-4 md:px-5 py-3.5 text-left hover:bg-slate-50/60"
                                  data-testid={`standard-term-toggle-${i}`}
                                  aria-expanded={isOpen}
                                >
                                  <span
                                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shrink-0 mt-0.5"
                                    data-testid={`standard-term-badge-${i}`}
                                  >
                                    Standard
                                  </span>
                                  <span className="flex-1 text-sm font-medium text-slate-800 leading-snug">
                                    {rf.title}
                                  </span>
                                  <ChevronDown
                                    className={`h-4 w-4 text-slate-400 mt-0.5 transition-transform ${
                                      isOpen ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                                {isOpen && (
                                  <div
                                    className="pl-4 md:pl-[4.75rem] pr-5 pb-4 space-y-1.5"
                                    data-testid={`standard-term-detail-${i}`}
                                  >
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                      {rf.detail}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      Reference: {rf.reference}
                                    </p>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </section>
                  )}

                  {/* Disclaimer */}
                  <div
                    className="mt-14 border border-slate-200 bg-slate-50 rounded-2xl p-5 text-sm text-slate-600 leading-relaxed"
                    data-testid="policy-disclaimer"
                  >
                    <strong className="text-slate-900">Important:</strong>{" "}
                    This is an automated plain-language explanation based only
                    on the text extracted from your uploaded PDF. It is not
                    legal or insurance advice. Coverage decisions rest with
                    your insurer — verify anything important against the
                    original policy wording before making a claim.
                    {data?.model && (
                      <span className="block mt-2 text-xs text-slate-400">
                        Analysed by {data.model}
                        {data.page_count ? ` · ${data.page_count} pages read` : ""}
                      </span>
                    )}
                  </div>

                  {/* Renewal reminder capture */}
                  <RenewalReminderCard uploadId={id} policyEndDate={r.period} />

                  {/* Feedback prompt */}
                  <FeedbackPrompt uploadId={id} />
                </>
              ) : null}
            </div>
          </>
        )}
      </div>

      <Footer />
      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(res) => {
          setUploadOpen(false);
          if (res?.id) navigate(`/policy/${res.id}`);
        }}
      />
      {status === "complete" && (
        <FloatingChat uploadId={id} ready={true} />
      )}
    </div>
  );
}
